<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[600px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-lg tw-my-5 tw-rounded-lg">
            <!-- Header -->
            <div class="tw-p-4 tw-border-b tw-border-solid tw-border-gray-200 tw-flex tw-justify-between tw-items-center">
                <h3 class="tw-text-lg tw-font-semibold tw-text-gray-800">{{ $t('forms.category.edit-title') }}</h3>
                <button @click="onCancel" class="tw-text-gray-400 hover:tw-text-gray-600 tw-duration-200">
                    <icon icon="solar:close-line-linear" class="tw-text-xl" />
                </button>
            </div>

            <!-- Form -->
            <form @submit.prevent="onSubmit" class="tw-p-4">

                
                <div class="tw-space-y-0">
                    <!-- Name Field -->
                    <FormField
                        v-model="form.name"
                        type="text"
                        :label="$t('forms.category.name')"
                        :placeholder="$t('forms.category.name-placeholder')"
                        :error="errors.name"
                        required
                    />

                    <!-- Type Field -->
                    <FormVueSelect
                        v-model="form.type"
                        :options="typeOptions"
                        :label="$t('forms.category.type')"
                        :placeholder="$t('forms.category.type-placeholder')"
                        :error="errors.type"
                        option-value="id"
                        option-label="name"
                    />

                    <!-- Description Field -->
                    <FormField
                        v-model="form.description"
                        type="textarea"
                        :label="$t('forms.category.description')"
                        :placeholder="$t('forms.category.description-placeholder')"
                        :error="errors.description"
                        rows="3"
                    />

                    

                    <!-- Status Field -->
                    <FormVueSelect
                        v-if="false"
                        v-model="form.status"
                        :options="statusOptions"
                        :label="$t('forms.category.status')"
                        :placeholder="$t('forms.category.status-placeholder')"
                        :error="errors.status"
                        option-value="id"
                        option-label="label"
                        clearable
                        disabled
                    />
                </div>

                <!-- Form Actions -->
                <div class="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-6 tw-pt-6 tw-border-t tw-border-gray-200">
                    <a-button 
                        type="button" 
                        intent="simple" 
                        @click="onCancel"
                        :disabled="isSubmitting"
                    >
                        {{ $t('buttons.cancel') }}
                    </a-button>
                    <a-button 
                        type="submit" 
                        intent="primary" 
                        class="tw-min-w-[160px]"
                        :loading="isSubmitting"
                        :disabled="isSubmitting"
                    >
                        {{ $t('buttons.update') }}
                    </a-button>
                </div>
            </form>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, reactive, watch, defineProps, defineEmits, nextTick, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormVueSelect from '@/components/form/FormVueSelect.vue';
import categoriesApi from '@/api/categories';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    category: {
        type: Object,
        default: null
    },
    types: {
        type: Array,
        default: () => []
    },
    type: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['close', 'updated']);

// Form data
const form = reactive({
    name: '',
    description: '',
    type: '',
    status: 'active'
});

// Form state
const isSubmitting = ref(false);
const errors = reactive({});

// Computed property to ensure form is always in sync
const shouldPopulateForm = computed(() => {
    return props.visible && props.category && Object.keys(props.category).length > 0;
});

// Options
const typeOptions = ref(props.types);

const statusOptions = ref([
    { id: 'active', label: t('common.active') },
    { id: 'inactive', label: t('common.inactive') }
]);

// Form validation
const validateForm = () => {
    errors.value = {};

    if (!form.name.trim()) {
        errors.name = t('errors.required', { field: t('forms.category.name') });
        return false;
    }

    if (form.name.trim().length < 2) {
        errors.name = t('errors.min-length', { field: t('forms.category.name'), min: 2 });
        return false;
    }

    if (form.description && form.description.trim().length > 500) {
        errors.description = t('errors.max-length', { field: t('forms.category.description'), max: 500 });
        return false;
    }

    return true;
};

// Methods
const onSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    isSubmitting.value = true;

    try {
        const response = await categoriesApi.admin.update(props.category.id, {
            name: form.name.trim(),
            description: form.description.trim() || null,
            type: form.type || null,
            status: form.status
        });

        if (response.data.status === 'SUCCESS') {
            emit('updated', response.data.category);
            resetForm();
        } else {
            useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        }
    } catch (error) {
        console.error('Error updating category:', error);
        
        if (error.response?.status === 422) {
            // Handle validation errors from server
            const serverErrors = error.response.data.errors;
            Object.keys(serverErrors).forEach(key => {
                errors[key] = serverErrors[key][0];
            });
        } else {
            useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        }
    } finally {
        isSubmitting.value = false;
    }
};

const onCancel = () => {
    if (!isSubmitting.value) {
        resetForm();
        emit('close');
    }
};

const resetForm = () => {
    form.name = '';
    form.description = '';
    form.type = '';
    form.status = 'active';
    Object.keys(errors).forEach(key => {
        errors[key] = '';
    });
};

const populateForm = () => {
    console.log('Populating form with category:', props.category); // Debug log
    
    if (props.category && typeof props.category === 'object') {
        // Ensure we're working with the actual category data
        const categoryData = props.category;
        
        // Populate form fields with proper fallbacks
        form.name = categoryData.name || categoryData.title || '';
        form.description = categoryData.description || categoryData.desc || '';
        form.type = categoryData.type || '';
        form.status = categoryData.status || 'active';
        
        // Clear any existing errors
        Object.keys(errors).forEach(key => {
            errors[key] = '';
        });
        
        console.log('Form populated successfully:', {
            name: form.name,
            description: form.description,
            type: form.type,
            status: form.status
        }); // Debug log
    } else {
        console.warn('No valid category data provided for form population');
    }
};

// Watch for modal visibility and category changes
watch(shouldPopulateForm, (newValue) => {
    console.log('Should populate form changed:', newValue, 'Category:', props.category); // Debug log
    if (newValue) {
        // Use nextTick to ensure DOM is updated
        nextTick(() => {
            populateForm();
        });
    } else if (!props.visible) {
        resetForm();
    }
}, { immediate: true });

// Also watch for category changes specifically
watch(() => props.category, (newValue) => {
    console.log('Category prop changed:', newValue); // Debug log
    if (newValue && props.visible) {
        // Use nextTick to ensure DOM is updated
        nextTick(() => {
            populateForm();
        });
    }
}, { immediate: true, deep: true });

// Watch for visible changes
watch(() => props.visible, (newValue) => {
    console.log('Modal visibility changed:', newValue, 'Category:', props.category); // Debug log
    if (newValue && props.category) {
        // Use nextTick to ensure DOM is updated
        nextTick(() => {
            populateForm();
        });
    } else if (!newValue) {
        resetForm();
    }
}, { immediate: true });
</script>

<style scoped>
/* Add any custom styles here */
</style> 